import { supabase } from '../lib/supabase';

/**
 * Professional Clients Management Service
 * Handles all operations for B2B client management, documents, orders, and EX1 tracking
 */

export const professionalClientsService = {
  /**
   * Get all professional clients with statistics
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of professional clients
   */
  async getAllClients(filters = {}) {
    try {
      let query = supabase?.from('professional_clients')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.clientType && filters?.clientType !== 'all') {
        query = query?.eq('client_type', filters?.clientType);
      }

      if (filters?.accountStatus && filters?.accountStatus !== 'all') {
        query = query?.eq('account_status', filters?.accountStatus);
      }

      if (filters?.search) {
        query = query?.or(`company_name.ilike.%${filters?.search}%,contact_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || [])?.map(client => ({
        id: client?.id,
        authUserId: client?.auth_user_id,
        companyName: client?.company_name,
        contactName: client?.contact_name,
        email: client?.email,
        phone: client?.phone,
        clientType: client?.client_type,
        siret: client?.siret,
        address: client?.address,
        assignedAdvisor: client?.assigned_advisor,
        dedicatedPhoneLine: client?.dedicated_phone_line,
        internalNotes: client?.internal_notes,
        registeredPickupAddresses: client?.registered_pickup_addresses || [],
        negotiatedRate: client?.negotiated_rate,
        totalShipments: client?.total_shipments || 0,
        accountStatus: client?.account_status,
        isActive: client?.is_active,
        createdAt: client?.created_at,
        updatedAt: client?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching professional clients:', error);
      throw error;
    }
  },

  /**
   * Get professional client by ID with full details
   * @param {string} clientId - Professional client ID
   * @returns {Promise<Object>} Professional client details
   */
  async getClientById(clientId) {
    try {
      const { data, error } = await supabase?.from('professional_clients')?.select('*')?.eq('id', clientId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        authUserId: data?.auth_user_id,
        companyName: data?.company_name,
        contactName: data?.contact_name,
        email: data?.email,
        phone: data?.phone,
        clientType: data?.client_type,
        siret: data?.siret,
        address: data?.address,
        assignedAdvisor: data?.assigned_advisor,
        dedicatedPhoneLine: data?.dedicated_phone_line,
        internalNotes: data?.internal_notes,
        registeredPickupAddresses: data?.registered_pickup_addresses || [],
        negotiatedRate: data?.negotiated_rate,
        totalShipments: data?.total_shipments || 0,
        accountStatus: data?.account_status,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching professional client:', error);
      throw error;
    }
  },

  /**
   * Update professional client information
   * @param {string} clientId - Professional client ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated client
   */
  async updateClient(clientId, updates) {
    try {
      const { data, error } = await supabase?.from('professional_clients')?.update({
          company_name: updates?.companyName,
          contact_name: updates?.contactName,
          phone: updates?.phone,
          client_type: updates?.clientType,
          siret: updates?.siret,
          address: updates?.address,
          assigned_advisor: updates?.assignedAdvisor,
          dedicated_phone_line: updates?.dedicatedPhoneLine,
          internal_notes: updates?.internalNotes,
          registered_pickup_addresses: updates?.registeredPickupAddresses,
          negotiated_rate: updates?.negotiatedRate,
          account_status: updates?.accountStatus,
          is_active: updates?.isActive,
          updated_at: new Date()?.toISOString()
        })?.eq('id', clientId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        companyName: data?.company_name,
        contactName: data?.contact_name,
        email: data?.email,
        phone: data?.phone,
        clientType: data?.client_type,
        accountStatus: data?.account_status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating professional client:', error);
      throw error;
    }
  },

  /**
   * Get all documents for a professional client
   * @param {string} clientId - Professional client ID
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of documents
   */
  async getClientDocuments(clientId, filters = {}) {
    try {
      let query = supabase?.from('pro_documents')?.select('*, shipment:shipment_id(id, reference)')?.eq('professional_client_id', clientId)?.order('created_at', { ascending: false });

      if (filters?.documentType && filters?.documentType !== 'all') {
        query = query?.eq('document_type', filters?.documentType);
      }

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || [])?.map(doc => ({
        id: doc?.id,
        professionalClientId: doc?.professional_client_id,
        shipmentId: doc?.shipment_id,
        documentType: doc?.document_type,
        fileUrl: doc?.file_url,
        fileName: doc?.file_name,
        fileSize: doc?.file_size,
        status: doc?.status,
        invoiceNumber: doc?.invoice_number,
        invoiceDate: doc?.invoice_date,
        invoiceAmountHt: doc?.invoice_amount_ht,
        invoiceAmountTtc: doc?.invoice_amount_ttc,
        invoiceStatus: doc?.invoice_status,
        ex1CertificateNumber: doc?.ex1_certificate_number,
        ex1Status: doc?.ex1_status,
        photoCaption: doc?.photo_caption,
        metadata: doc?.metadata,
        uploadedBy: doc?.uploaded_by,
        createdAt: doc?.created_at,
        shipment: doc?.shipment ? {
          id: doc?.shipment?.id,
          reference: doc?.shipment?.reference
        } : null
      }));
    } catch (error) {
      console.error('Error fetching client documents:', error);
      throw error;
    }
  },

  /**
   * Upload document for professional client
   * @param {string} clientId - Professional client ID
   * @param {File} file - File to upload
   * @param {Object} metadata - Document metadata
   * @returns {Promise<Object>} Uploaded document
   */
  async uploadDocument(clientId, file, metadata) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${metadata?.documentType}-${Date.now()}.${fileExt}`;
      const filePath = `${clientId}/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase?.storage?.from('pro-documents')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Create document record
      const { data, error } = await supabase?.from('pro_documents')?.insert({
          professional_client_id: clientId,
          shipment_id: metadata?.shipmentId || null,
          document_type: metadata?.documentType,
          file_url: filePath,
          file_name: file?.name,
          file_size: file?.size,
          status: 'active',
          invoice_number: metadata?.invoiceNumber || null,
          invoice_date: metadata?.invoiceDate || null,
          invoice_amount_ht: metadata?.invoiceAmountHt || null,
          invoice_amount_ttc: metadata?.invoiceAmountTtc || null,
          invoice_status: metadata?.invoiceStatus || null,
          ex1_certificate_number: metadata?.ex1CertificateNumber || null,
          ex1_status: metadata?.ex1Status || null,
          photo_caption: metadata?.photoCaption || null,
          metadata: metadata?.additionalMetadata || {}
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        fileUrl: filePath,
        fileName: file?.name,
        documentType: data?.document_type,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for private document
   * @param {string} filePath - File path in storage
   * @returns {Promise<string>} Signed URL
   */
  async getDocumentUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage?.from('pro-documents')?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting document URL:', error);
      return null;
    }
  },

  /**
   * Get pending orders for processing
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of pending orders
   */
  async getPendingOrders(filters = {}) {
    try {
      let query = supabase?.from('pro_pending_orders')?.select('*, professional_client:professional_client_id(id, company_name, contact_name, email, phone)')?.order('created_at', { ascending: false });

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.orderType && filters?.orderType !== 'all') {
        query = query?.eq('order_type', filters?.orderType);
      }

      if (filters?.needsEx1) {
        query = query?.eq('needs_ex1', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || [])?.map(order => ({
        id: order?.id,
        professionalClientId: order?.professional_client_id,
        orderType: order?.order_type,
        serviceType: order?.service_type,
        volumeRequested: order?.volume_requested,
        quantityRequested: order?.quantity_requested,
        needsEx1: order?.needs_ex1,
        pickupAddress: order?.pickup_address,
        pickupCity: order?.pickup_city,
        pickupPostalCode: order?.pickup_postal_code,
        deliveryAddress: order?.delivery_address,
        deliveryCity: order?.delivery_city,
        deliveryCountry: order?.delivery_country,
        specialInstructions: order?.special_instructions,
        estimatedAmount: order?.estimated_amount,
        status: order?.status,
        assignedTo: order?.assigned_to,
        quoteReference: order?.quote_reference,
        quoteSentAt: order?.quote_sent_at,
        createdAt: order?.created_at,
        professionalClient: order?.professional_client ? {
          id: order?.professional_client?.id,
          companyName: order?.professional_client?.company_name,
          contactName: order?.professional_client?.contact_name,
          email: order?.professional_client?.email,
          phone: order?.professional_client?.phone
        } : null
      }));
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      throw error;
    }
  },

  /**
   * Update order status
   * @param {string} orderId - Order ID
   * @param {string} status - New status
   * @param {Object} additionalData - Additional data to update
   * @returns {Promise<Object>} Updated order
   */
  async updateOrderStatus(orderId, status, additionalData = {}) {
    try {
      const { data, error } = await supabase?.from('pro_pending_orders')?.update({
          status,
          quote_reference: additionalData?.quoteReference || null,
          quote_sent_at: additionalData?.quoteSentAt || null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', orderId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        quoteReference: data?.quote_reference,
        quoteSentAt: data?.quote_sent_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  /**
   * Get EX1 certificates for processing
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of EX1 certificates
   */
  async getEX1Certificates(filters = {}) {
    try {
      // Use the view that includes computed delay fields
      let query = supabase?.from('pro_ex1_tracking_with_delays')?.select('*, professional_client:professional_client_id(id, company_name, email), shipment:shipment_id(id, reference)')?.order('created_at', { ascending: false });

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.alertOnly) {
        query = query?.gte('days_pending', 7);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || [])?.map(cert => ({
        id: cert?.id,
        professionalClientId: cert?.professional_client_id,
        shipmentId: cert?.shipment_id,
        documentId: cert?.document_id,
        certificateNumber: cert?.certificate_number,
        status: cert?.status,
        requestedAt: cert?.requested_at,
        stampedAt: cert?.stamped_at,
        deliveredAt: cert?.delivered_at,
        notes: cert?.notes,
        daysPending: cert?.days_pending,
        isDelayed: cert?.is_delayed,
        alertDaysPending: cert?.days_pending, // Alias for backward compatibility
        createdAt: cert?.created_at,
        professionalClient: cert?.professional_client ? {
          id: cert?.professional_client?.id,
          companyName: cert?.professional_client?.company_name,
          email: cert?.professional_client?.email
        } : null,
        shipment: cert?.shipment ? {
          id: cert?.shipment?.id,
          reference: cert?.shipment?.reference
        } : null
      }));
    } catch (error) {
      console.error('Error fetching EX1 certificates:', error);
      throw error;
    }
  },

  /**
   * Update EX1 certificate status
   * @param {string} certId - Certificate ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated certificate
   */
  async updateEX1Status(certId, updates) {
    try {
      const { data, error } = await supabase?.from('pro_ex1_tracking')?.update({
          status: updates?.status,
          approval_date: updates?.approvalDate || null,
          rejection_reason: updates?.rejectionReason || null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', certId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        approvalDate: data?.approval_date,
        rejectionReason: data?.rejection_reason,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating EX1 status:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time updates for professional clients
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription channel
   */
  subscribeToProClients(callback) {
    const channel = supabase?.channel('pro-clients-realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'professional_clients'
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Subscribe to real-time updates for professional documents
   * @param {string} clientId - Professional client ID
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription channel
   */
  subscribeToProDocuments(clientId, callback) {
    const channel = supabase?.channel('pro-documents-realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pro_documents',
          filter: `professional_client_id=eq.${clientId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time channel
   * @param {Object} channel - Subscription channel
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};